import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  listMinGuarantees,
  createMinGuarantee,
  updateMinGuarantee,
  deleteMinGuarantee,
} from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { useBranch } from "@/context/BranchContext";

const emptyForm = { name: "", amount: "", active: true };

export default function MinGuaranteesTab() {
  const { branchId } = useBranch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listMinGuarantees());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ name: p.name, amount: p.amount, active: p.active !== false });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt < 0) {
      toast.error("Amount must be >= 0");
      return;
    }
    try {
      const payload = { name: form.name.trim(), amount: amt, active: form.active };
      if (editingId) {
        await updateMinGuarantee(editingId, payload);
        toast.success("Preset updated");
      } else {
        await createMinGuarantee(payload);
        toast.success("Preset added");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMinGuarantee(deleteId);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="min-guarantees-tab">
      <div className="flex items-end justify-between">
        <p className="text-xs text-zinc-500">
          Create preset amounts you can pick from when recording a daily entry.
        </p>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg"
              data-testid="add-min-guarantee-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Preset
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-md">
            <form onSubmit={submit}>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {editingId ? "Edit Preset" : "Add Preset"}
                </DialogTitle>
                <DialogDescription>
                  Name and amount used as a minimum earning guarantee on daily entries.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Standard / Peak Day / Rainy Day"
                    data-testid="mg-name-input"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Amount (INR)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">
                      ₹
                    </span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="currency-input pl-7"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="mg-amount-input"
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-zinc-200 px-4 py-2.5">
                  <div>
                    <div className="text-sm font-medium">Active</div>
                    <div className="text-xs text-zinc-500">Show this preset when creating entries.</div>
                  </div>
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="mg-active-switch"
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button
                  type="submit"
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="mg-save-btn"
                >
                  {editingId ? "Save changes" : "Add preset"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-zinc-200 p-10 text-center text-zinc-500">
          Loading presets...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-zinc-200 p-10 text-center">
          <div className="mx-auto h-10 w-10 rounded-full bg-emerald-50 grid place-items-center mb-3">
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
          </div>
          <div className="font-display text-lg">No presets yet</div>
          <p className="text-sm text-zinc-500 mt-1">
            Add a preset like "Standard ₹500" so you can pick it while creating a daily entry.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>Name</Th>
                <Th className="text-right">Amount</Th>
                <Th>Status</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-zinc-100 last:border-b-0"
                  data-testid={`mg-row-${p.id}`}
                >
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-right tabular font-medium text-emerald-700">
                    {formatINR(p.amount)}
                  </td>
                  <td className="px-4 py-3">
                    {p.active !== false ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-zinc-200 text-zinc-500">
                        Inactive
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(p)}
                      data-testid={`mg-edit-${p.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleteId(p.id)}
                      className="text-rose-600 hover:text-rose-700"
                      data-testid={`mg-delete-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this preset?</AlertDialogTitle>
            <AlertDialogDescription>
              Historical entries that used this preset will keep their saved amount.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mg-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              data-testid="mg-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th
    className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}
  >
    {children}
  </th>
);
